import { signal, computed } from '@preact/signals';
import { swApi } from './wb.js';

// State for cached videos
const cachedVideos = signal([]);
const autoCachedVideos = signal([]);
const storageInfo = signal(null);

// Map for quick access
const cachedVideoIds = computed(() => new Set(cachedVideos.value.map((v) => v.id)));

// Check if a video is cached
export function isVideoCached(videoId) {
  return cachedVideoIds.value.has(videoId);
}

// Enable/disable video caching
export function enableVideoCaching() {
  swApi.enableVideoCaching();
}

export function disableVideoCaching() {
  swApi.disableVideoCaching();
}

// Refresh the list of cached videos
export async function refreshCachedVideos() {
  try {
    const [offlineResponse, autoResponse] = await Promise.all([swApi.getAllCachedVideos(), swApi.getAutoCachedVideos()]);
    cachedVideos.value = offlineResponse?.data?.videos || [];
    autoCachedVideos.value = autoResponse?.data?.videos || [];
  } catch (error) {
    console.error('Error refreshing cached videos:', error);
  }
}

// Refresh storage info
export async function refreshStorageInfo() {
  try {
    const response = await swApi.getStorageInfo();
    if (response?.data?.storageInfo) {
      storageInfo.value = response.data.storageInfo;
    }
  } catch (error) {
    console.error('Error refreshing storage info:', error);
  }
}

// Add a video to offline cache
export async function addVideoToOfflineCache(id, title) {
  try {
    const response = await swApi.addVideoToOfflineCache(id, title);

    if (response?.data?.success) {
      await refreshCachedVideos();
      await refreshStorageInfo();
    } else {
      throw new Error(response?.data?.error || 'Failed to add video');
    }
  } catch (error) {
    console.error('Error adding video to cache:', error);
    throw error;
  }
}

// Remove a video from offline cache
export async function removeVideoFromOfflineCache(id) {
  try {
    await swApi.removeVideoFromOfflineCache(id);
    await refreshCachedVideos();
    await refreshStorageInfo();
  } catch (error) {
    console.error('Error removing video from cache:', error);
    throw error;
  }
}

// Clear the video caches (cached videos)
export async function clearCachedVideos() {
  try {
    const response = await swApi.clearCachedVideos();
    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Failed to clear video cache');
    }
    await refreshCachedVideos();
    await refreshStorageInfo();
  } catch (error) {
    console.error('Error clearing video cache:', error);
    throw error;
  }
}

// Clear the offline-video cache (downloaded videos)
export async function clearDownloadedVideos() {
  try {
    const response = await swApi.clearDownloadedVideos();
    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Failed to clear offline-video cache');
    }
    await refreshCachedVideos();
    await refreshStorageInfo();
  } catch (error) {
    console.error('Error clearing offline-video cache:', error);
    throw error;
  }
}

// Exporter le state
export const cache = {
  videos: cachedVideos,
  autoVideos: autoCachedVideos,
  storageInfo,
};
